import { Box, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Currency, formatMoney, formatNumber, formatPercent } from '@nemuru-eng/js-api-client';

import { DurationMap } from '@/components/modules/Metrics/LenderRanking/maps/duration.map';
import { LENDER_RANKING_TOP } from '@/components/modules/Metrics/LenderRanking/maps/lenderRanking.map';
import { LenderRankingRow } from '@/components/modules/Metrics/LenderRanking/types/LenderRankingRow';
import { LenderRankingSummaryType } from '@/components/modules/Metrics/LenderRanking/types/LenderRankingSummaryType';
import { MetricVariation } from '@/components/modules/Metrics/shared/components/MetricVariation';
import { ScoreCardMetricKey } from '@/components/modules/Metrics/shared/types/ScoreCardMetricKey';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { LenderIconAvatar } from '@/components/shared/components/Logo/LenderLogo/LenderIcon';
import { Text } from '@/components/shared/components/Text';
import { useDefaultTable } from '@/components/shared/hooks/useDefaultTable';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useScreen } from '@/hooks/useScreen';

const SUMMARY_LABELS: Record<LenderRankingSummaryType, string> = {
  [LenderRankingSummaryType.TOP]: 'LENDER_RANKING_TOP',
  [LenderRankingSummaryType.REST]: 'LENDER_RANKING_REST',
  [LenderRankingSummaryType.TOTAL]: 'LENDER_RANKING_TOTAL',
};

interface LenderRankingTableStyleProps {
  title: string;
  body: string;
  separator: string;
  rule: string;
  summaryFill: string;
}

const useStyles = makeStyles({
  emphasized: (props: LenderRankingTableStyleProps) => ({
    fontWeight: 700,
    color: props.title,
  }),
  footer: (props: LenderRankingTableStyleProps) => ({
    '& .MuiTableCell-footer': {
      fontSize: '0.875rem',
      lineHeight: 1.43,
      borderBottom: 'none',
      backgroundColor: 'transparent',
      color: props.body,
    },
  }),
  compositionRow: (props: LenderRankingTableStyleProps) => ({
    '& .MuiTableCell-root': {
      backgroundColor: props.summaryFill,
      borderBottom: `1px solid ${props.separator}`,
    },
  }),
  lastCompositionRow: {
    '& .MuiTableCell-root': {
      borderBottom: 'none',
    },
  },
  totalRow: (props: LenderRankingTableStyleProps) => ({
    '& .MuiTableCell-footer': {
      borderTop: `2px solid ${props.rule}`,
      fontWeight: 700,
      color: props.title,
      paddingTop: 16,
      fontSize: '1rem',
    },
  }),
  firstFooterRow: (props: LenderRankingTableStyleProps) => ({
    '& .MuiTableCell-root': {
      borderTop: `2px solid ${props.rule}`,
    },
  }),
});

interface LenderRankingTableProps {
  lenders: LenderRankingRow[];
  summaries: LenderRankingRow[];
  currency: Currency;
  metricKey: ScoreCardMetricKey;
}

export const LenderRankingTable = ({ lenders, summaries, currency, metricKey }: LenderRankingTableProps) => {
  const { palette } = useCustomTheme();
  const { lg } = useScreen();
  const { getTextIdTranslation, hasTranslation } = useTranslation();
  const { classes } = useDefaultTable({ tableElementId: 'lender-ranking-table' });
  const styles = useStyles({
    title: palette.text.title ?? palette.text.primary,
    body: palette.text.primary,
    separator: palette.grey[200],
    rule: palette.grey[400],
    summaryFill: palette.grey[50],
  });

  const composition = summaries.filter(
    (row) => row.summaryType === LenderRankingSummaryType.TOP || row.summaryType === LenderRankingSummaryType.REST,
  );
  const total = summaries.find((row) => row.summaryType === LenderRankingSummaryType.TOTAL);
  const maxShare = Math.max(...lenders.map((row) => row.share));

  const formatValue = (value: number) =>
    metricKey === ScoreCardMetricKey.totalLoansNumber ? formatNumber(value, 0) : formatMoney({ value, currency }, 0);

  const formalization = (seconds?: number) => {
    if (seconds === undefined) return '-';
    const { value, unit } = DurationMap.toUnit(seconds);
    return <Text tId={unit} dynamicDataTexts={[value]} />;
  };

  const header = (tId: string, tooltipTId?: string) => (
    <TableCell align="right">
      <Box display="flex" alignItems="center" justifyContent="flex-end" gridGap={4}>
        <Text tId={tId} />
        {tooltipTId && (
          <Tooltip title={getTextIdTranslation(tooltipTId) as string}>
            <Box display="flex" alignItems="center" justifyContent="center">
              <Icon size={16} color={palette.grey[500]}>
                info
              </Icon>
            </Box>
          </Tooltip>
        )}
      </Box>
    </TableCell>
  );

  const metricCells = (row: LenderRankingRow) => {
    const isSummary = Boolean(row.summaryType);
    const isTotal = row.summaryType === LenderRankingSummaryType.TOTAL;

    return (
      <>
        <TableCell align="right" className={isSummary && !isTotal ? undefined : styles.emphasized}>
          {formatValue(row.value)}
        </TableCell>
        <TableCell align="right">
          {isTotal ? null : (
            <Box display="flex" alignItems="center" justifyContent="flex-end" gridGap={8}>
              {formatPercent(row.share, isSummary ? 1 : 0)}
              {lg && !isSummary && (
                <Box width={64} height={6} bgcolor={palette.grey[100]} borderRadius={3} overflow="hidden">
                  <Box
                    height="100%"
                    width={`${maxShare ? Math.min(100, (row.share / maxShare) * 100) : 0}%`}
                    bgcolor={palette.text.title}
                  />
                </Box>
              )}
            </Box>
          )}
        </TableCell>
        <TableCell align="right">
          <Box display="flex" justifyContent="flex-end">
            <MetricVariation variation={row.variation} />
          </Box>
        </TableCell>
        <TableCell align="right">{row.approval === undefined ? '-' : formatPercent(row.approval, 0)}</TableCell>
        <TableCell align="right">
          {row.averageTicket === undefined ? '-' : formatMoney({ value: row.averageTicket, currency }, 0)}
        </TableCell>
        <TableCell align="right">{formalization(row.averageFormalizationSeconds)}</TableCell>
      </>
    );
  };

  const summaryLabel = (row: LenderRankingRow) => (
    <Text
      tId={SUMMARY_LABELS[row.summaryType as LenderRankingSummaryType]}
      dynamicDataTexts={[String(LENDER_RANKING_TOP)]}
    />
  );

  return (
    <Box overflow="auto">
      <Table id="lender-ranking-table" className={classes.root}>
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>
              <Text tId="LENDER_RANKING_LENDER" />
            </TableCell>
            {header(
              metricKey === ScoreCardMetricKey.totalLoansNumber ? 'LENDER_RANKING_LOANS' : 'LENDER_RANKING_AMOUNT',
            )}
            {header('LENDER_RANKING_SHARE')}
            {header('LENDER_RANKING_VARIATION')}
            {header('LENDER_RANKING_APPROVAL', 'LENDER_RANKING_APPROVAL_TOOLTIP')}
            {header('LENDER_RANKING_AVERAGE_TICKET')}
            {header('LENDER_RANKING_FORMALIZATION', 'LENDER_RANKING_FORMALIZATION_TOOLTIP')}
          </TableRow>
        </TableHead>
        <TableBody>
          {lenders.map((row, index) => (
            <TableRow key={row.key}>
              <TableCell>
                <Font variant="body2" color="textSecondary">
                  <strong>{index + 1}</strong>
                </Font>
              </TableCell>
              <TableCell>
                <Box display="flex" alignItems="center" gridGap={12}>
                  {row.lenderName && <LenderIconAvatar lenderName={row.lenderName} />}
                  <Font variant="body2" color="title">
                    <strong>
                      {row.lenderLabel && hasTranslation(row.lenderLabel)
                        ? getTextIdTranslation(row.lenderLabel)
                        : row.lenderLabel}
                    </strong>
                  </Font>
                </Box>
              </TableCell>
              {metricCells(row)}
            </TableRow>
          ))}
        </TableBody>
        {(composition.length > 0 || total) && (
          <TableFooter className={styles.footer}>
            {composition.map((row, index) => (
              <TableRow
                key={row.key}
                className={[
                  styles.compositionRow,
                  index === 0 ? styles.firstFooterRow : '',
                  index === composition.length - 1 ? styles.lastCompositionRow : '',
                ].join(' ')}
              >
                <TableCell colSpan={2}>
                  <Box pl={3}>{summaryLabel(row)}</Box>
                </TableCell>
                {metricCells(row)}
              </TableRow>
            ))}
            {total && (
              <TableRow className={[styles.totalRow, composition.length === 0 ? styles.firstFooterRow : ''].join(' ')}>
                <TableCell colSpan={2}>
                  <Box pl={3}>{summaryLabel(total)}</Box>
                </TableCell>
                {metricCells(total)}
              </TableRow>
            )}
          </TableFooter>
        )}
      </Table>
    </Box>
  );
};
